import { DSDivider, DSFlex, DSIcon, DSIconColorType, DSIconName, DSSpacer, DSText, DSTextColorType } from '@zoe/ds-web'
import React from 'react'
import { ResponsiveImageType } from 'react-datocms/image'

import { FragmentComponent } from '@/types/graphql'

import { DSHide } from '@/components/ds/Responsive/Hide/Hide'

import { responsiveImageBlock } from '@/lib/api/graphql/utils/commonBlocks'

import { processTypographyMarkdown } from '../DesignSystem/Typography'

export type FeatureRecord = {
  __typename: 'FeatureRecord'
  id: string
  description: string
  iconOrImage: {
    __typename: 'DsIconRecord' | 'SimpleImageRecord'
    name?: string
    size?: number
    color?: string
    image?: {
      responsiveImage: ResponsiveImageType
    }
    rounded?: boolean
  }
}

export type FeatureGridRecord = FeatureRecord[]

const fragment = `
featureGrid {
  __typename
  id
  description
  iconOrImage {
    __typename
    ... on DsIconRecord {
      id
      name
      size
      color
    }
    ... on SimpleImageRecord {
      __typename
      id
      rounded
      image {
        ${responsiveImageBlock()}
      }
    }
  }
}
`

const Feature = ({ record, color }: { record: FeatureRecord; color: DSTextColorType }) => {
  const renderIcon = () => (
    <DSIcon
      name={record.iconOrImage.name as DSIconName}
      size={72}
      color={color === 'primary-inverse' ? 'inverse' : 'primary'}
    />
  )

  const renderImage = () => (
    <img
      src={record.iconOrImage.image?.responsiveImage.src || ''}
      alt={record.iconOrImage.image?.responsiveImage.alt || ''}
      style={{ width: '114px', borderRadius: record.iconOrImage.rounded ? '16px' : '0%' }}
    />
  )

  const renderDescription = () => (
    <DSText as="p" variant="fluid-paragraph-200" align="center" color={color || 'primary'}>
      {processTypographyMarkdown({ content: record.description })}
    </DSText>
  )

  return (
    <DSFlex direction="column" gap={16} alignItems="center" justifyContent="flex-start" flex="1" width="80%">
      {record.iconOrImage.__typename === 'DsIconRecord' && renderIcon()}
      {record.iconOrImage.__typename === 'SimpleImageRecord' && renderImage()}
      {renderDescription()}
    </DSFlex>
  )
}

export const FeatureGridBlock: FragmentComponent<{ typographyColor: DSTextColorType }, FeatureGridRecord> = ({
  record,
  typographyColor,
}) => {
  return (
    <DSFlex
      gap={16}
      breakConfig={{
        maxViewportDirection: 'row',
        breakAt: {
          breakPoint: 'm',
          direction: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 32,
        },
      }}
    >
      {[...record].map((item, index) => (
        <>
          <Feature key={item.id} record={item} color={typographyColor} />
          {index < record.length - 1 && (
            <DSDivider direction="vertical" variant={typographyColor === 'primary' ? 'tertiary' : 'secondary'} />
          )}
          <DSHide above="m">
            <DSDivider
              direction="horizontal"
              variant={typographyColor === 'primary' ? 'tertiary' : 'secondary'}
              width={215}
            />
          </DSHide>
        </>
      ))}
    </DSFlex>
  )
}

FeatureGridBlock.fragment = fragment
FeatureGridBlock.recordName = 'FeatureGridRecord'
